/**
 * Dynamic Free Gift Promo v1
 * Configured via shop metafields (window.freeGiftConfig).
 * When customer buys the trigger product, they get one free gift per unit.
 * The free gift cannot be purchased independently.
 */

(function() {
  'use strict';

  const CONFIG = window.freeGiftConfig;
  if (!CONFIG || CONFIG.triggerVariantId == null || CONFIG.freeVariantId == null) {
    console.log('[FREE-GIFT] No active promo configured. Script inactive.');
    return;
  }

  console.log('[FREE-GIFT] Config loaded — trigger:', CONFIG.triggerVariantId, '| free:', CONFIG.freeVariantId);

  let isProcessing = false;
  let isSelfUpdate = false;
  let lastCartToken = null;
  let lastCartSignature = null;
  let syncTimeout = null;

  // Content signature: changes whenever items/quantities/total change.
  // cart.token stays constant between item updates, so it can't detect
  // cart changes — that left the old poll a permanent no-op.
  function cartSignature(cart) {
    if (!cart || !cart.items) return '0';
    const parts = cart.items.map((i) => i.key + 'x' + i.quantity);
    return cart.item_count + '|' + cart.total_price + '|' + parts.join(',');
  }

  // ============================================
  // CART OPERATIONS
  // ============================================

  async function getCart() {
    try {
      const response = await fetch('/cart.js', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('[FREE-GIFT] Error fetching cart:', error);
      return null;
    }
  }

  function isFreeProduct(item) {
    return (
      item.variant_id === CONFIG.freeVariantId ||
      (item.properties && item.properties._free_gift === 'true')
    );
  }

  function calculateEligibleQuantity(cart) {
    if (!cart || !cart.items) return 0;

    return cart.items.reduce((total, item) => {
      if (isFreeProduct(item)) {
        return total;
      }

      if (item.variant_id === CONFIG.triggerVariantId) {
        console.log(`[FREE-GIFT] Eligible item: ${item.title} (variant: ${item.variant_id}, qty: ${item.quantity})`);
        return total + item.quantity;
      }
      return total;
    }, 0);
  }

  function getFreeProductInCart(cart) {
    if (!cart || !cart.items) return { quantity: 0, key: null, rogueItems: [] };

    let managedItem = null;
    const rogueItems = [];

    for (const item of cart.items) {
      if (item.variant_id === CONFIG.freeVariantId) {
        if (item.properties && item.properties._free_gift === 'true') {
          managedItem = item;
        } else {
          // Same variant added manually (no _free_gift property) — rogue
          rogueItems.push({ key: item.key, quantity: item.quantity });
        }
      }
    }

    return {
      quantity: managedItem ? managedItem.quantity : 0,
      key: managedItem ? managedItem.key : null,
      rogueItems
    };
  }

  async function updateCartItem(key, quantity) {
    try {
      isSelfUpdate = true;
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!response.ok) throw new Error('Failed to update cart');
      return await response.json();
    } catch (error) {
      console.error('[FREE-GIFT] Error updating cart:', error);
      return null;
    }
  }

  async function addToCart(variantId, quantity) {
    try {
      isSelfUpdate = true;
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: { '_free_gift': 'true' }
        })
      });
      if (!response.ok) throw new Error('Failed to add to cart');
      return await response.json();
    } catch (error) {
      console.error('[FREE-GIFT] Error adding to cart:', error);
      return null;
    }
  }

  // ============================================
  // SYNC LOGIC
  // ============================================

  async function syncFreeProduct() {
    if (isProcessing) {
      return { synced: false };
    }

    isProcessing = true;
    isSelfUpdate = true;

    try {
      const cart = await getCart();
      if (!cart) {
        isProcessing = false;
        isSelfUpdate = false;
        return { synced: false };
      }

      const eligibleQuantity = calculateEligibleQuantity(cart);
      const freeProduct = getFreeProductInCart(cart);

      // Remove any rogue (manually-added) copies of the free product
      for (const rogue of freeProduct.rogueItems) {
        console.log('[FREE-GIFT] Removing rogue free product (qty:', rogue.quantity, ')...');
        await updateCartItem(rogue.key, 0);
      }

      console.log('[FREE-GIFT] Eligible:', eligibleQuantity, '| Current free qty:', freeProduct.quantity);

      if (eligibleQuantity === freeProduct.quantity && freeProduct.rogueItems.length === 0) {
        console.log('[FREE-GIFT] Already in sync');
        lastCartToken = cart.token;
        lastCartSignature = cartSignature(cart);
        isProcessing = false;
        setTimeout(() => { isSelfUpdate = false; }, 500);
        return { synced: true, eligibleQuantity, freeQuantity: freeProduct.quantity };
      }

      if (eligibleQuantity === 0 && freeProduct.key) {
        console.log('[FREE-GIFT] Removing free product (no trigger in cart)...');
        await updateCartItem(freeProduct.key, 0);
      } else if (freeProduct.key && freeProduct.quantity !== eligibleQuantity) {
        console.log('[FREE-GIFT] Updating free product to:', eligibleQuantity);
        await updateCartItem(freeProduct.key, eligibleQuantity);
      } else if (eligibleQuantity > 0 && !freeProduct.key) {
        console.log('[FREE-GIFT] Adding free product:', eligibleQuantity);
        await addToCart(CONFIG.freeVariantId, eligibleQuantity);
      }

      const updatedCart = await getCart();
      if (updatedCart) {
        lastCartToken = updatedCart.token;
        lastCartSignature = cartSignature(updatedCart);
      }

      await refreshCartUI();

      isProcessing = false;
      setTimeout(() => { isSelfUpdate = false; }, 1000);

      return { synced: true, eligibleQuantity, freeQuantity: eligibleQuantity };

    } catch (error) {
      console.error('[FREE-GIFT] Sync error:', error);
      isProcessing = false;
      isSelfUpdate = false;
      return { synced: false, error };
    }
  }

  // ============================================
  // ENFORCED CHECKOUT VALIDATION
  // ============================================

  async function validateAndProceedToCheckout() {
    console.log('[FREE-GIFT] Validating cart before checkout...');

    const cart = await getCart();
    if (!cart) {
      console.error('[FREE-GIFT] Could not fetch cart for validation');
      return false;
    }

    const eligibleQuantity = calculateEligibleQuantity(cart);
    const freeProduct = getFreeProductInCart(cart);

    // Remove any rogue (manually-added) copies of the free product
    let hadRogues = false;
    for (const rogue of freeProduct.rogueItems) {
      console.warn('[FREE-GIFT] Removing rogue free product at checkout (qty:', rogue.quantity, ')...');
      await updateCartItem(rogue.key, 0);
      hadRogues = true;
    }

    console.log('[FREE-GIFT] CHECKOUT VALIDATION - Eligible:', eligibleQuantity, '| Free in cart:', freeProduct.quantity);

    if (freeProduct.quantity > eligibleQuantity) {
      console.warn('[FREE-GIFT] Excess free items detected! Correcting cart...');

      if (eligibleQuantity === 0) {
        await updateCartItem(freeProduct.key, 0);
      } else {
        await updateCartItem(freeProduct.key, eligibleQuantity);
      }

      await refreshCartUI();

      showNotification('La cantidad de productos gratis ha sido ajustada segun tu carrito.');

      return false;
    }

    if (freeProduct.quantity < eligibleQuantity) {
      if (freeProduct.key) {
        await updateCartItem(freeProduct.key, eligibleQuantity);
      } else {
        await addToCart(CONFIG.freeVariantId, eligibleQuantity);
      }
      await refreshCartUI();
    }

    if (hadRogues) {
      await refreshCartUI();
      showNotification('La cantidad de productos gratis ha sido ajustada segun tu carrito.');
      return false;
    }

    return true;
  }

  function showNotification(message) {
    const existing = document.getElementById('free-gift-notification');
    if (existing) existing.remove();

    const notification = document.createElement('div');
    notification.id = 'free-gift-notification';
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #ff6b6b;
        color: white;
        padding: 15px 25px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 999999;
        font-size: 14px;
        font-weight: 500;
        max-width: 90%;
        text-align: center;
      ">
        ${message}
      </div>
    `;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 4000);
  }

  function scheduleSyncDebounced(delay = 300) {
    if (isSelfUpdate) {
      return;
    }

    if (syncTimeout) clearTimeout(syncTimeout);
    syncTimeout = setTimeout(() => syncFreeProduct(), delay);
  }

  // ============================================
  // UI REFRESH
  // ============================================

  async function refreshCartUI() {
    console.log('[FREE-GIFT] Refreshing cart UI...');

    try {
      const sectionsToRefresh = [];

      if (document.querySelector('cart-drawer')) {
        sectionsToRefresh.push('cart-drawer');
      }

      if (document.querySelector('cart-items')) {
        sectionsToRefresh.push('main-cart-items');
      }

      const cartFooter = document.querySelector('.cart__footer') ||
                         document.querySelector('#main-cart-footer') ||
                         document.querySelector('[id*="main-cart-footer"]');
      if (cartFooter) {
        const sectionEl = cartFooter.closest('[id^="shopify-section-"]');
        if (sectionEl) {
          sectionsToRefresh.push(sectionEl.id.replace('shopify-section-', ''));
        } else {
          sectionsToRefresh.push('main-cart-footer');
        }
      }

      for (const sectionId of sectionsToRefresh) {
        await refreshSection(sectionId);
      }

      updateCartBubble();

    } catch (error) {
      console.error('[FREE-GIFT] Refresh error:', error);
    }
  }

  async function refreshSection(sectionId) {
    try {
      const response = await fetch(`/?section_id=${sectionId}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const currentSection = document.querySelector(`[id$="__${sectionId}"]`) ||
                            document.getElementById(`shopify-section-${sectionId}`);

      const newSection = doc.querySelector(`[id$="__${sectionId}"]`) ||
                        doc.getElementById(`shopify-section-${sectionId}`) ||
                        doc.body.firstElementChild;

      if (currentSection && newSection) {
        currentSection.innerHTML = newSection.innerHTML;
      } else {
        const elementMap = {
          'cart-drawer': 'cart-drawer',
          'main-cart-items': 'cart-items',
          'main-cart-footer': '.cart__footer'
        };

        const selector = elementMap[sectionId];
        if (selector) {
          const currentEl = document.querySelector(selector);
          const newEl = doc.querySelector(selector);
          if (currentEl && newEl) {
            currentEl.innerHTML = newEl.innerHTML;
          }
        }
      }
    } catch (error) {
      console.error(`[FREE-GIFT] Error refreshing section ${sectionId}:`, error);
    }
  }

  async function updateCartBubble() {
    const cart = await getCart();
    if (!cart) return;

    document.querySelectorAll('.cart-count-bubble').forEach(bubble => {
      const span = bubble.querySelector('span[aria-hidden="true"]');
      if (span) span.textContent = cart.item_count;
    });
  }

  // ============================================
  // EVENT LISTENERS
  // ============================================

  function setupEventListeners() {

    // Intercept XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url) {
      this._url = url;
      return originalXHROpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function() {
      this.addEventListener('load', function() {
        if (isSelfUpdate) return;

        if (this._url && (
          this._url.includes('/cart/add') ||
          this._url.includes('/cart/change') ||
          this._url.includes('/cart/update') ||
          this._url.includes('/cart/clear')
        )) {
          console.log('[FREE-GIFT] XHR cart change detected');
          scheduleSyncDebounced(400);
        }
      });
      return originalXHRSend.apply(this, arguments);
    };

    // Intercept fetch API
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

      return originalFetch.apply(this, args).then(response => {
        if (isSelfUpdate) return response;

        if (url.includes('/cart/add') ||
            url.includes('/cart/change') ||
            url.includes('/cart/update') ||
            url.includes('/cart/clear')) {
          console.log('[FREE-GIFT] Fetch cart change detected');
          scheduleSyncDebounced(400);
        }
        return response;
      });
    };

    // Native cart events
    document.addEventListener('cart:item-added', () => {
      if (isSelfUpdate) return;
      scheduleSyncDebounced(300);
    });

    document.addEventListener('cart:updated', () => {
      if (isSelfUpdate || isProcessing) return;
      scheduleSyncDebounced(300);
    });

    // Quantity input changes
    document.addEventListener('change', (e) => {
      if (isSelfUpdate) return;

      if (e.target.closest('quantity-input') ||
          e.target.name === 'updates[]' ||
          e.target.classList.contains('quantity__input')) {
        scheduleSyncDebounced(500);
      }
    });

    // Cart button clicks
    document.addEventListener('click', (e) => {
      if (isSelfUpdate) return;

      const target = e.target;

      if (target.closest('button[name="plus"]') ||
          target.closest('button[name="minus"]') ||
          target.closest('.quantity__button') ||
          target.closest('cart-remove-button') ||
          target.closest('.cart-item__remove')) {
        scheduleSyncDebounced(600);
      }

      if (target.closest('[type="submit"][name="add"]') ||
          target.closest('.product-form__submit') ||
          target.closest('button[data-add-to-cart]')) {
        scheduleSyncDebounced(800);
      }
    });

    // ============================================
    // ENFORCED CHECKOUT INTERCEPTION
    // ============================================

    document.addEventListener('click', async (e) => {
      const checkoutBtn = e.target.closest(
        '[name="checkout"], ' +
        'button[name="checkout"], ' +
        '.cart__checkout-button, ' +
        '.cart__checkout, ' +
        'a[href="/checkout"], ' +
        'a[href*="/checkout"], ' +
        'input[name="checkout"], ' +
        '[data-checkout-button]'
      );

      if (checkoutBtn) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const originalContent = checkoutBtn.innerHTML;
        const originalValue = checkoutBtn.value;

        if (checkoutBtn.tagName === 'INPUT') {
          checkoutBtn.value = 'Validando...';
        } else {
          checkoutBtn.innerHTML = '<span>Validando...</span>';
        }
        checkoutBtn.disabled = true;
        checkoutBtn.style.opacity = '0.7';
        checkoutBtn.style.pointerEvents = 'none';

        let allValid = await validateAndProceedToCheckout();

        // Run any additional registered validators (e.g. threshold-gift-promo)
        if (allValid) {
          for (const validator of (window.__checkoutValidators || [])) {
            const valid = await validator();
            if (!valid) { allValid = false; break; }
          }
        }

        if (allValid) {
          console.log('[FREE-GIFT] All validations passed, proceeding to checkout');
          window.location.href = '/checkout';
        } else {
          console.log('[FREE-GIFT] Cart was corrected, user must click checkout again');

          if (checkoutBtn.tagName === 'INPUT') {
            checkoutBtn.value = originalValue;
          } else {
            checkoutBtn.innerHTML = originalContent;
          }
          checkoutBtn.disabled = false;
          checkoutBtn.style.opacity = '';
          checkoutBtn.style.pointerEvents = '';
        }

        return false;
      }
    }, true);

    // Block form submissions to checkout
    document.addEventListener('submit', async (e) => {
      const form = e.target;

      if (form.action?.includes('/checkout') ||
          form.querySelector('[name="checkout"]')) {

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        console.log('[FREE-GIFT] Checkout form submission intercepted');

        let allValid = await validateAndProceedToCheckout();
        if (allValid) {
          for (const validator of (window.__checkoutValidators || [])) {
            const valid = await validator();
            if (!valid) { allValid = false; break; }
          }
        }

        if (allValid) {
          window.location.href = '/checkout';
        }

        return false;
      }
    }, true);

    // Block keyboard shortcuts (Enter on checkout button)
    document.addEventListener('keydown', async (e) => {
      if (e.key === 'Enter') {
        const activeElement = document.activeElement;
        if (activeElement?.closest('[name="checkout"], .cart__checkout-button, a[href="/checkout"]')) {
          e.preventDefault();
          e.stopPropagation();

          let allValid = await validateAndProceedToCheckout();
          if (allValid) {
            for (const validator of (window.__checkoutValidators || [])) {
              const valid = await validator();
              if (!valid) { allValid = false; break; }
            }
          }
          if (allValid) {
            window.location.href = '/checkout';
          }

          return false;
        }
      }
    }, true);

    // Periodic check on cart page. Keys on a content signature (not
    // cart.token, which never changes between item updates) so it reliably
    // catches every cart change Dawn makes.
    if (window.location.pathname.includes('/cart')) {
      setInterval(async () => {
        if (!isProcessing && !isSelfUpdate) {
          const cart = await getCart();
          if (!cart) return;
          const sig = cartSignature(cart);
          if (sig !== lastCartSignature) {
            lastCartSignature = sig;
            lastCartToken = cart.token;

            const eligibleQuantity = calculateEligibleQuantity(cart);
            const freeProduct = getFreeProductInCart(cart);

            if (eligibleQuantity !== freeProduct.quantity || freeProduct.rogueItems.length > 0) {
              syncFreeProduct();
            }
          }
        }
      }, 750);
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  // Dawn's theme JS captures window.fetch before our defer scripts load,
  // so our fetch/XHR interceptors miss Dawn's cart updates.
  // MutationObserver catches the section re-render that follows every cart change.
  let sectionChangeTimer = null;

  function onSectionRerender() {
    if (isProcessing || isSelfUpdate) return;
    if (sectionChangeTimer) clearTimeout(sectionChangeTimer);
    sectionChangeTimer = setTimeout(() => {
      console.log('[FREE-GIFT] Section re-render detected, running sync');
      syncFreeProduct();
    }, 400);
  }

  function setupSectionObservers() {
    // Use actual DOM elements, not section IDs (which have unpredictable template prefixes)
    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl) {
      new MutationObserver(() => onSectionRerender())
        .observe(cartItemsEl, { childList: true, subtree: true });
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(() => onSectionRerender())
        .observe(drawer, { childList: true, subtree: true });
    }
  }

  function init() {
    console.log('[FREE-GIFT] Initializing dynamic free gift promo...');

    setupEventListeners();
    setupSectionObservers();
    syncFreeProduct();

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !isSelfUpdate) {
        scheduleSyncDebounced(500);
      }
    });

    console.log('[FREE-GIFT] Ready.');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.FreeGiftPromo = {
    sync: syncFreeProduct,
    validate: validateAndProceedToCheckout,
    refresh: refreshCartUI,
    getCart: getCart
  };

})();